import {
  ErrorCode,
  Purchases,
  PurchasesError,
  type Package,
} from "@revenuecat/purchases-js";
import { ENV } from "@/util/env";

/** Thrown when the user closes the checkout without paying. Not an error state. */
export class PurchaseCancelledError extends Error {
  constructor() {
    super("Purchase cancelled.");
    this.name = "PurchaseCancelledError";
  }
}

/** False when VITE_REVENUECAT_WEB_KEY is unset — billing UI should hide itself. */
export function isBillingConfigured(): boolean {
  return !!ENV.REVENUECAT_WEB_KEY;
}

/**
 * Configure the RevenueCat SDK for a signed-in user. app_user_id is ALWAYS the
 * Firebase UID (backend invariant). Safe to call repeatedly; switches user if
 * a different UID is already configured. No-op for anonymous visitors or when
 * the key is missing.
 */
export function configurePurchases(firebaseUid: string): void {
  if (!ENV.REVENUECAT_WEB_KEY || !firebaseUid) return;
  if (Purchases.isConfigured()) {
    const shared = Purchases.getSharedInstance();
    if (shared.getAppUserId() !== firebaseUid) {
      shared.changeUser(firebaseUid).catch(() => {
        // Non-fatal: purchase flows re-check configuration before running.
      });
    }
    return;
  }
  Purchases.configure({ apiKey: ENV.REVENUECAT_WEB_KEY, appUserId: firebaseUid });
}

export interface ProPackages {
  monthly: Package | null;
  annual: Package | null;
}

/** Monthly + annual packages from the current ("default") offering. */
export async function getProOffering(): Promise<ProPackages> {
  const offerings = await Purchases.getSharedInstance().getOfferings();
  const current = offerings.current ?? offerings.all["default"] ?? null;
  return { monthly: current?.monthly ?? null, annual: current?.annual ?? null };
}

/**
 * Run the RC Web Billing checkout for a package. Resolves on success.
 * Throws PurchaseCancelledError on user-cancel, the raw error otherwise.
 */
export async function purchasePro(pkg: Package, customerEmail?: string): Promise<void> {
  try {
    await Purchases.getSharedInstance().purchase({
      rcPackage: pkg,
      customerEmail,
    });
  } catch (err) {
    if (err instanceof PurchasesError && err.errorCode === ErrorCode.UserCancelledError) {
      throw new PurchaseCancelledError();
    }
    throw err;
  }
}

/** URL of RC's hosted manage/cancel portal for the current subscriber, if any. */
export async function getManagementUrl(): Promise<string | null> {
  if (!Purchases.isConfigured()) return null;
  const info = await Purchases.getSharedInstance().getCustomerInfo();
  return info.managementURL;
}
